<script lang="ts">
  import autoAnimate from "@formkit/auto-animate";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import { expandShorthandHex, getTextColorAndPrimaryVariants } from "app/utils/colors";
  import { fade } from "svelte/transition";
  import { createEventDispatcher } from "svelte";
  import { Check, HashTag } from "$components/+blocks/OVIcon";

  const DEFAULT_PALETTE: string[] = [
    "#00CA2C",
    "#4EFF91",
    "#3FCCF5",
    "#6776FF",
    "#B152FC",
    "#EC5EFC",
    "#FF5E48",
    "#FF7A00",
    "#FFEE57",
  ];

  export let color: string;
  export let palette: string[] = DEFAULT_PALETTE;

  const dispatch = createEventDispatcher();

  let showCustomColor: boolean = false;
  let textColor: string;
  let userInputColor = color;

  $: textColor = getTextColorAndPrimaryVariants(color).textColor;

  function onInput() {
    // remove the # from the start of the hex color
    const inputHex = userInputColor.replace(/^#/, "");
    // expand shorthand hex color code (e.g., 'abc' to 'aabbcc')
    const expandedHex = expandShorthandHex(inputHex);
    // add the # back to the start of the hex color
    color = `#${expandedHex}`;
  }

  // check if the hex color is a valid 3 or 6 digits hex color
  function validHex(hex: string) {
    return /^#([0-9a-f]{3}){1,2}$/i.test(hex);
  }
</script>

<div
  in:fade={{ duration: 80 }}
  out:fade={{ duration: 100, delay: 100 }}
  use:autoAnimate
  class="glossy-effect max-h-[53px] max-w-[100vw] overflow-hidden rounded-md border-[0.5px] border-black/20 px-3 py-1 shadow-lg dark:border-white/20"
>
  {#if showCustomColor}
    <div class="flex w-max items-center gap-4 px-1 py-2 *:shrink-0">
      <button
        class="flex size-7 items-center justify-center rounded-full border-[1.5px] border-neutral-700 transition-[shadow]
          {validHex(color)
          ? 'hover:border-neutral-900 hover:shadow-[0px_0px_3px_1px] hover:shadow-inherit'
          : ''}"
        style:background-color={color}
        style="--tw-shadow-color: {color};"
        on:click={() => dispatch("select", { color })}
        aria-label={color}
      >
        {#if validHex(color)}
          <span style:color={textColor} transition:fade={{ duration: 150 }}>
            <OVIcon icon={Check} size="size-5" />
          </span>
        {/if}
      </button>

      <div class="text-neutral-500 dark:text-neutral-300/50">
        <span class="text-sm">HEX</span>
        <input
          type="text"
          maxlength="7"
          class="transition-color ml-1.5 h-7 w-[90px] border-x-0 border-b-[0.5px] border-t-0 border-b-transparent bg-transparent text-sm focus:ring-0"
          style:border-color={color}
          bind:value={userInputColor}
          on:input={onInput}
        />
      </div>
      <button
        type="button"
        class="ml-7 flex size-7 flex-wrap items-center justify-center rounded border-[0.5px] border-black/10 p-1.5 transition-colors hover:border-black/30 dark:border-white/10 dark:hover:border-white/30"
        on:click|stopPropagation={() => (showCustomColor = false)}
      >
        {#each palette as color}
          <span style:background-color={color} class="size-1 rounded-full">&nbsp;</span>
        {/each}
      </button>
    </div>
  {:else}
    <div class="flex w-full items-center gap-5">
      <div
        class="scrollbar-thin scrollbar-stable scrollbar-glossy-light dark:scrollbar-glossy-dark flex w-full items-center gap-5 overflow-x-auto px-1 py-2"
      >
        {#each palette as paletteColor, index}
          <button
            class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-[1.5px] border-neutral-500 transition-[shadow] hover:border-neutral-700 hover:shadow-[0px_0px_3px_1px] hover:shadow-inherit dark:border-neutral-900/70 dark:hover:border-neutral-900"
            style:background-color={paletteColor}
            style="--tw-shadow-color: {paletteColor};"
            on:click={() => {
              color = paletteColor;
              dispatch("select", { color });
            }}
            in:fade|global={{ duration: 80, delay: 25 * index }}
            aria-label={paletteColor}
          >
            {#if paletteColor === color}
              <span style:color={textColor}>
                <OVIcon icon={Check} size="size-5" />
              </span>
            {/if}
          </button>
        {/each}
      </div>
      <div class="h-full w-[1px] bg-black/15 dark:bg-white/15">&nbsp;</div>
      <button
        type="button"
        class="shrink-0 rounded border-[0.5px] border-black/10 p-1.5 transition-colors hover:border-black/30 dark:border-white/10 dark:hover:border-white/30"
        on:click={() => (showCustomColor = true)}
      >
        <OVIcon icon={HashTag} size="size-3" class="text-neutral-500 dark:text-neutral-400" />
      </button>
    </div>
  {/if}
</div>
